/**
 * Bundle Builder
 * Shows every configured product as a fixed bundle line, lets shoppers pick
 * variant/color swatches, and updates the stacked preview whenever a swatch
 * changes. Adds every line to the cart at once via the Storefront cart AJAX
 * endpoint.
 *
 * Works with the bundle-builder section (sections/bundle-builder.liquid).
 */
if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        this.items = []; // one fixed item per configured product
        this.itemEls = {}; // item key -> DOM element
        this.productsById = {};
      }

      connectedCallback() {
        try {
          this.products = JSON.parse(
            this.querySelector('[data-bundle-products-json]').textContent
          );
          this.strings = JSON.parse(
            this.querySelector('[data-bundle-strings]').textContent
          );
        } catch (error) {
          console.error('Failed to parse bundle builder data:', error);
          return;
        }

        this.products.forEach((product) => {
          this.productsById[product.id] = product;
        });

        this.discountPercentage = parseInt(this.dataset.discountPercentage, 10) || 0;

        this.totalEl = this.querySelector('[data-bundle-total]');
        this.totalOriginalEl = this.querySelector('[data-bundle-total-original]');
        this.discountBadgeEl = this.querySelector('[data-bundle-discount-badge]');
        this.errorEl = this.querySelector('[data-bundle-error]');
        this.checkoutBtn = this.querySelector('[data-bundle-checkout]');
        this.addCartBtn = this.querySelector('[data-bundle-add-cart]');
        this.addCartBtnLabel = this.querySelector('.bundle-builder__add-cart-label');
        this.previewEl = this.querySelector('[data-bundle-preview]');
        this.previewLayersEl = this.querySelector('[data-bundle-preview-layers]');
        this.previewLoadingEl = this.querySelector('[data-bundle-preview-loading]');
        this.previewZoomBtn = this.querySelector('[data-bundle-preview-zoom]');
        this.lightboxEl = this.querySelector('[data-bundle-lightbox]');
        this.lightboxLayersEl = this.querySelector('[data-bundle-lightbox-layers]');
        this.previewLayers = {};
        this.pendingPreviewLoads = 0;
        this.stackImagesByInstance = {};
        this.stackZIndexByInstance = {};

        this.productEls = Array.from(this.querySelectorAll('[data-bundle-item]'));
        this.productEls.forEach((el) => {
          const instanceId = parseInt(el.dataset.instanceId, 10);
          const productId = parseInt(el.dataset.productId, 10);
          if (!Number.isFinite(instanceId) || !Number.isFinite(productId)) return;
          const product = this.productsById[productId];
          if (!product) return;

          const stackZIndex = parseInt(el.dataset.stackZIndex, 10);
          if (Number.isFinite(stackZIndex)) {
            this.stackZIndexByInstance[instanceId] = stackZIndex;
          }
          const script = el.querySelector('[data-stack-images-json]');
          if (script) {
            try {
              this.stackImagesByInstance[instanceId] = JSON.parse(script.textContent);
            } catch (error) {
              this.stackImagesByInstance[instanceId] = {};
            }
          }

          const variant = this.getDefaultVariant(product);
          if (!variant) return;
          const key = `instance-${instanceId}`;
          this.items.push({
            key,
            instanceId,
            productId,
            variantId: variant.id,
            title: product.title,
            url: product.url,
            variantTitle: variant.title,
            price: variant.price,
            compareAtPrice: variant.compare_at_price || 0,
            image: variant.featured_image || product.image,
            stackImage: this.getStackImage(instanceId, variant.id),
            available: variant.available,
            quantity: 1,
          });
          this.itemEls[key] = el;
        });

        this.bindEvents();
        this.syncDefaultSelections();
        this.render();
      }

      bindEvents() {
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-bundle-preview-zoom]') || event.target.closest('[data-bundle-preview]')) {
            this.openLightbox();
            return;
          }

          if (event.target.closest('[data-bundle-lightbox-close]')) {
            this.closeLightbox();
          }
        });

        this.addEventListener('change', (event) => {
          const optionInput = event.target.closest('input[type="radio"][data-option-position]');
          if (!optionInput) return;
          const itemEl = optionInput.closest('[data-bundle-item]');
          if (!itemEl) return;
          const item = this.getItemByInstance(parseInt(itemEl.dataset.instanceId, 10));
          if (item) this.updateItemFromVariant(item);
        });

        this.checkoutBtn.addEventListener('click', () => this.addToCart(true));
        this.addCartBtn.addEventListener('click', () => this.addToCart(false));

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.lightboxEl && !this.lightboxEl.hidden) {
            this.closeLightbox();
          }
        });
      }

      // ----- Selection helpers -----

      getItemByInstance(instanceId) {
        return this.items.find((item) => item.instanceId === instanceId) || null;
      }

      syncDefaultSelections() {
        // Liquid's per-product "selected" state is unreliable when several
        // products render inside the same section loop, so make sure every
        // option has a checked radio on load. Existing selections (for
        // example from a ?variant= URL) are left untouched.
        this.items.forEach((item) => {
          const el = this.itemEls[item.key];
          if (!el) return;
          const product = this.productsById[item.productId];
          if (!product) return;
          const variant = product.variants.find((v) => v.id === item.variantId);
          if (!variant) return;

          el.querySelectorAll('[data-bundle-option]').forEach((fieldset) => {
            const inputs = Array.from(
              fieldset.querySelectorAll('input[type="radio"][data-option-value]')
            );
            if (inputs.some((input) => input.checked)) return;

            const position = parseInt(fieldset.dataset.optionPosition, 10);
            const expected = variant['option' + position];
            const target =
              inputs.find((input) => input.dataset.optionValue === expected) ||
              inputs.find((input) => !input.disabled) ||
              inputs[0];
            if (target) target.checked = true;
          });
        });
      }

      getDefaultVariant(product) {
        if (!product || !product.variants.length) return null;
        return product.variants.find((v) => v.available) || product.variants[0];
      }

      getVariantId(item) {
        const product = this.productsById[item.productId];
        if (!product) return null;

        const el = this.itemEls[item.key];
        const selectedOptions = {};
        if (el) {
          el
            .querySelectorAll('input[type="radio"][data-option-position]:checked')
            .forEach((input) => {
              selectedOptions[parseInt(input.dataset.optionPosition, 10)] = input.value;
            });
        }

        if (Object.keys(selectedOptions).length > 0) {
          const variant = this.findVariantByOptions(product, selectedOptions);
          if (variant) return variant.id;
        }

        const variant = this.getDefaultVariant(product);
        return variant ? variant.id : null;
      }

      getStackImage(instanceId, variantId) {
        const map = this.stackImagesByInstance[instanceId];
        return map && map[variantId] ? map[variantId] : null;
      }

      findVariantByOptions(product, selectedOptions) {
        // Exact match first.
        for (const variant of product.variants) {
          let match = true;
          for (const [pos, val] of Object.entries(selectedOptions)) {
            if (variant['option' + pos] !== val) {
              match = false;
              break;
            }
          }
          if (match) return variant;
        }

        // Fallback: first available variant matching the first selected option.
        if (selectedOptions[1]) {
          return (
            product.variants.find(
              (variant) => variant.option1 === selectedOptions[1] && variant.available
            ) || null
          );
        }

        return null;
      }

      updateItemFromVariant(item) {
        const product = this.productsById[item.productId];
        const variantId = this.getVariantId(item);
        if (!product || !variantId) return;
        const variant = product.variants.find((v) => v.id === variantId);
        if (!variant) return;

        item.variantId = variant.id;
        item.variantTitle = variant.title;
        item.price = variant.price;
        item.compareAtPrice = variant.compare_at_price || 0;
        item.image = variant.featured_image || product.image;
        item.stackImage = this.getStackImage(item.instanceId, variant.id);
        item.available = variant.available;

        this.render();
      }

      getTotalQuantity() {
        let total = 0;
        this.items.forEach((item) => {
          total += item.quantity;
        });
        return total;
      }

      getTotalPrice() {
        let total = 0;
        this.items.forEach((item) => {
          total += item.price * item.quantity;
        });
        return total;
      }

      // ----- Rendering -----

      render() {
        this.renderItems();
        this.renderPreview();
        this.renderTotals();
      }

      renderItems() {
        this.items.forEach((item) => this.renderItem(item));
      }

      renderItem(item) {
        const el = this.itemEls[item.key];
        if (!el) return;

        const image = el.querySelector('[data-item-image]');
        if (image && item.image) image.src = item.image;

        const variantLine = el.querySelector('[data-item-variant-line]');
        const priceEl = el.querySelector('[data-item-price]');
        if (priceEl) {
          const discounted =
            this.discountPercentage > 0
              ? Math.round((item.price * (100 - this.discountPercentage)) / 100)
              : item.price;
          priceEl.classList.toggle('price--on-sale', this.discountPercentage > 0);
          const originalEl = priceEl.querySelector('[data-item-original-price]');
          const discountedEl = priceEl.querySelector('[data-item-discounted-price]');
          if (discountedEl) discountedEl.textContent = this.formatMoney(discounted);
          if (originalEl) originalEl.textContent = this.formatMoney(item.price);
        }

        if (variantLine) {
          variantLine.innerHTML = '';
          if (item.variantTitle && item.variantTitle !== 'Default Title') {
            const variantName = document.createElement('span');
            variantName.className = 'bundle-builder-item__variant';
            variantName.textContent = item.variantTitle;
            variantLine.appendChild(variantName);
          }
        }
      }

      renderPreview() {
        if (!this.previewEl || !this.previewLayersEl) return;

        const keys = this.items.map((item) => item.key);
        if (keys.length === 0) {
          this.previewEl.hidden = true;
          this.pendingPreviewLoads = 0;
          if (this.previewLoadingEl) this.previewLoadingEl.hidden = true;
          Object.values(this.previewLayers).forEach((layer) => layer.remove());
          this.previewLayers = {};
          return;
        }

        this.previewEl.hidden = false;

        // Remove layers for items that are no longer part of the bundle.
        Object.keys(this.previewLayers).forEach((key) => {
          if (!this.items.some((item) => item.key === key)) {
            this.previewLayers[key].remove();
            delete this.previewLayers[key];
          }
        });

        keys.forEach((key, index) => {
          const item = this.items.find((entry) => entry.key === key);
          let layer = this.previewLayers[key];
          if (!layer) {
            layer = document.createElement('div');
            layer.className = 'bundle-builder__preview-layer';
            layer.dataset.previewLayer = '';
            this.previewLayersEl.appendChild(layer);
            this.previewLayers[key] = layer;
          }
          // Layer order comes from the block's z-index setting (higher = on top).
          layer.style.zIndex = String(
            this.stackZIndexByInstance[item.instanceId] || index + 1
          );

          const url = item.stackImage;
          if (!url) {
            layer.querySelectorAll('img').forEach((img) => img.remove());
            return;
          }

          const currentImg = layer.querySelector('img');
          if (currentImg && currentImg.dataset.src === url) return;

          const next = document.createElement('img');
          next.dataset.src = url;
          next.alt = '';
          next.decoding = 'async';
          let revealed = false;
          let swapped = false;
          const finishSwap = () => {
            if (swapped) return;
            swapped = true;
            if (currentImg) currentImg.remove();
            // The new image has fully replaced the old one by now, so the
            // loading indicator can disappear.
            this.hidePreviewLoading();
          };
          const reveal = () => {
            if (revealed) return;
            revealed = true;
            // Keep the old image visible while the new one loads, then
            // crossfade: fade the old one out at the same time the new one
            // fades in. Keep the loading indicator visible until the new
            // image has fully faded in, then remove the old image.
            if (currentImg) currentImg.classList.remove('is-shown');
            next.classList.add('is-shown');
            next.addEventListener('transitionend', (event) => {
              if (event.target === next && event.propertyName === 'opacity') {
                finishSwap();
              }
            });
            // Fallback in case the transition does not fire.
            setTimeout(finishSwap, 600);
          };
          next.addEventListener('load', reveal, { once: true });
          next.addEventListener('error', reveal, { once: true });

          this.showPreviewLoading();
          layer.appendChild(next);
          next.src = url;
          if (next.complete) {
            // Give the browser a frame to paint the new image at opacity 0 so
            // the crossfade transition always runs, even from cache.
            requestAnimationFrame(() => requestAnimationFrame(reveal));
          }
        });
      }

      showPreviewLoading() {
        this.pendingPreviewLoads += 1;
        if (this.previewLoadingEl) this.previewLoadingEl.hidden = false;
      }

      hidePreviewLoading() {
        this.pendingPreviewLoads = Math.max(0, this.pendingPreviewLoads - 1);
        if (this.pendingPreviewLoads === 0 && this.previewLoadingEl) {
          this.previewLoadingEl.hidden = true;
        }
      }

      openLightbox() {
        if (!this.lightboxEl || !this.lightboxLayersEl || this.items.length === 0) return;
        this.renderLightbox();
        this.lightboxEl.hidden = false;
        document.body.style.overflow = 'hidden';
        const closeBtn = this.lightboxEl.querySelector('[data-bundle-lightbox-close]');
        if (closeBtn) closeBtn.focus();
      }

      closeLightbox() {
        if (!this.lightboxEl) return;
        this.lightboxEl.hidden = true;
        document.body.style.overflow = '';
        if (this.previewZoomBtn) this.previewZoomBtn.focus();
      }

      renderLightbox() {
        if (!this.lightboxLayersEl) return;
        this.lightboxLayersEl
          .querySelectorAll('[data-lightbox-layer]')
          .forEach((layer) => layer.remove());

        this.items.forEach((item, index) => {
          const url = item.stackImage;
          if (!url) return;
          const layer = document.createElement('div');
          layer.className = 'bundle-builder__lightbox-layer';
          layer.dataset.lightboxLayer = '';
          layer.style.zIndex = String(this.stackZIndexByInstance[item.instanceId] || index + 1);
          const img = document.createElement('img');
          img.src = url;
          img.alt = '';
          img.decoding = 'async';
          layer.appendChild(img);
          this.lightboxLayersEl.appendChild(layer);
        });
      }

      renderTotals() {
        const totalQuantity = this.getTotalQuantity();
        const total = this.getTotalPrice();
        const discounted =
          this.discountPercentage > 0
            ? Math.round((total * (100 - this.discountPercentage)) / 100)
            : total;

        this.totalEl.textContent = this.formatMoney(discounted);

        if (this.discountPercentage > 0 && total > 0) {
          if (this.totalOriginalEl) {
            this.totalOriginalEl.hidden = false;
            this.totalOriginalEl.textContent = this.formatMoney(total);
          }
          if (this.discountBadgeEl) {
            this.discountBadgeEl.hidden = false;
            this.discountBadgeEl.textContent = `-${this.discountPercentage}%`;
          }
        } else {
          if (this.totalOriginalEl) this.totalOriginalEl.hidden = true;
          if (this.discountBadgeEl) this.discountBadgeEl.hidden = true;
        }

        const disabled = totalQuantity === 0;
        this.checkoutBtn.disabled = disabled;
        this.addCartBtn.disabled = disabled;
      }

      showError(message) {
        if (!message) {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
          return;
        }
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      // ----- Add to cart -----

      async addToCart(redirectToCheckout = false) {
        const submitBtn = redirectToCheckout ? this.checkoutBtn : this.addCartBtn;
        if (submitBtn.disabled) return;

        const items = [];
        this.items.forEach((item) => {
          items.push({ id: item.variantId, quantity: item.quantity });
        });

        if (items.length === 0) return;

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        this.showError(null);
        submitBtn.classList.add('btn--loading');
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.disabled = true;

        try {
          const response = await fetch(FoxTheme.routes.cart_add_url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items,
              sections: sectionsToBundle.join(','),
              sections_url: window.location.pathname,
            }),
          });
          const parsedState = await response.json();

          if (parsedState.status) {
            this.showError(parsedState.description || parsedState.message);
            return;
          }

          document.dispatchEvent(
            new CustomEvent('product-ajax:added', {
              detail: { product: parsedState },
            })
          );

          if (redirectToCheckout) {
            // Redirect straight to checkout with the completed bundle.
            window.location.href = FoxTheme.routes.checkout_url || '/checkout';
            return;
          }

          // Add to cart: keep the shopper on the page and open the cart drawer.
          const cartDrawer = document.querySelector('cart-drawer');
          if (!cartDrawer || FoxTheme.settings?.cartType === 'page') {
            window.location = FoxTheme.routes.cart_url;
            return;
          }

          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, {
              ...FoxTheme.utils.fetchConfig(),
            })
          ).json();
          cartJson['sections'] = parsedState['sections'];

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-builder',
            cart: cartJson,
          });

          if (typeof cartDrawer.show === 'function') {
            cartDrawer.show(submitBtn);
          }

          if (this.addCartBtnLabel) {
            this.addCartBtnLabel.textContent = (this.strings.added || this.strings.addToCart).toUpperCase();
            setTimeout(() => {
              if (this.addCartBtnLabel) {
                this.addCartBtnLabel.textContent = this.strings.addToCart.toUpperCase();
              }
            }, 2000);
          }
        } catch (error) {
          console.error('Bundle add to cart error:', error);
          this.showError(this.strings.addError);
        } finally {
          submitBtn.classList.remove('btn--loading');
          submitBtn.removeAttribute('aria-disabled');
          this.renderTotals();
        }
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(
            cents,
            (window.FoxTheme?.settings?.moneyFormat || '').replace(
              'amount_with_comma_separator',
              '{{amount}}'
            )
          );
        }
        return '$' + (cents / 100).toFixed(2);
      }
    }
  );
}
